// src/App.js
import React, { Suspense, lazy } from "react";
import { Routes, Route, Outlet, Navigate } from "react-router-dom";
import { Box } from "@mui/material";
import AppShell from "./app/AppShell";
import { useAuth } from "./context/auth";
import { LOGIN_OPEN } from "./config/loginGate";

// 玄関（/）だけは即時ロード。初回表示の一文を最速で届ける
import HomePage from "./pages/HomePage";
import TopPage from "./pages/TopPage";

// 以降は遅延ロード（コード分割）。三次元・地図・adminを初回バンドルから外す
const LoginPage = lazy(() => import("./pages/LoginPage"));
const ShrineDetailPage = lazy(() => import("./pages/ShrineDetailPage"));
const ShrineRegisterPage = lazy(() => import("./pages/ShrineRegisterPage"));
const AdminDeitiesPage = lazy(() => import("./pages/AdminDeitiesPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const AdminStylePage = lazy(() => import("./pages/AdminStylePage"));
const AdminNearbyPage = lazy(() => import("./pages/AdminNearbyPage"));
const AdminUsersPage = lazy(() => import("./pages/AdminUsersPage"));
const AdminUserDetailPage = lazy(() => import("./pages/AdminUserDetailPage"));
const AdminFeedbackPage = lazy(() => import("./pages/AdminFeedbackPage"));
const AdminShrinePinPage = lazy(() => import("./pages/AdminShrinePinPage"));
const AdminShrineReportsPage = lazy(() => import("./pages/AdminShrineReportsPage"));
const AdminShrinesPage = lazy(() => import("./pages/AdminShrinesPage"));
const NearbyShrinesPage = lazy(() => import("./pages/NearbyShrinesPage"));
const MyPage = lazy(() => import("./pages/MyPage"));
const MeStatsPage = lazy(() => import("./pages/MeStatsPage"));
const MeEnLogPage = lazy(() => import("./pages/MeEnLogPage"));
const MeMilePage = lazy(() => import("./pages/MeMilePage"));
const AdminMilePage = lazy(() => import("./pages/AdminMilePage"));
const AdminExchangeItemsPage = lazy(() => import("./pages/AdminExchangeItemsPage"));
const VisitHistoryPage = lazy(() => import("./pages/VisitHistoryPage"));
const AstroMapPage = lazy(() => import("./pages/AstroMapPage"));
const RecommendPage = lazy(() => import("./pages/RecommendPage"));
const DeitiesPage = lazy(() => import("./pages/DeitiesPage"));
const DeityDetailPage = lazy(() => import("./pages/DeityDetailPage"));
const BlogListPage = lazy(() => import("./pages/BlogListPage"));
const BlogEditPage = lazy(() => import("./pages/BlogEditPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const AdminChatCostSimPage = lazy(() => import("./pages/AdminChatCostSimPage"));
const AdminDevCostSimPage = lazy(() => import("./pages/AdminDevCostSimPage"));
const KeidaiFieldPage = lazy(() => import("./pages/KeidaiFieldPage"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage"));
const UnseiPage = lazy(() => import("./pages/UnseiPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const CreditsPage = lazy(() => import("./pages/CreditsPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const TochiPage = lazy(() => import("./pages/TochiPage"));

// admin ロールのみ通すガード（未ロード中は何も出さない）
// ログインの入口を断っている間（LOGIN_OPEN=false）、未ログインには閉じるページ。玄関へ戻す
// （/nearby → 参拝画面 → ログイン、の導線が残っていたため。2026-11-03）
function GuestClosed({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!LOGIN_OPEN && !user) return <Navigate to="/" replace />;
  return children;
}

function RequireAdmin() {
  const { loading, isAdmin } = useAuth();
  if (loading) return null;
  if (!isAdmin) return <Navigate to="/" replace />;
  return <Outlet />;
}

// フルスクリーンページ（/keidai）読み込み中の暗転
function DarkFallback() {
  return <Box sx={{ position: "fixed", inset: 0, bgcolor: "#12100d" }} />;
}

export default function App() {
  return (
    <Routes>
      {/* AppShell配下にページを差し込む（ページ側のSuspenseはAppShell内のOutlet周りにある） */}
      <Route element={<AppShell />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/shrines/register" element={<ShrineRegisterPage />} />
        <Route path="/nearby" element={<GuestClosed><NearbyShrinesPage /></GuestClosed>} />
        <Route path="/home" element={<HomePage />} />
        {/* ログイン後のトップ（2026-10-13 新設）。/ と /home はログイン済みならここへ転送する */}
        <Route path="/top" element={<TopPage />} />
        {/* 「今日のあなたへ」は 2026-10-13 一旦停止（ページは src/pages/TodayPage.js に残置）。復活時はここと AppShell のナビを戻す */}
        <Route path="/today" element={<Navigate to="/" replace />} />
        <Route path="/recommend" element={<RecommendPage />} />
        <Route path="/deities" element={<DeitiesPage />} />
        <Route path="/deities/:deityName" element={<DeityDetailPage />} />
        <Route path="/shrines/:shrineId" element={<ShrineDetailPage />} />
        <Route path="/me" element={<MyPage />} />
        <Route path="/me/stats" element={<MeStatsPage />} />
        <Route path="/me/enlog" element={<MeEnLogPage />} />
        <Route path="/me/miles" element={<MeMilePage />} />
        <Route path="/me/history" element={<VisitHistoryPage />} />
        <Route path="/me/blog" element={<BlogListPage />} />
        <Route path="/me/blog/new" element={<BlogEditPage />} />
        <Route path="/me/blog/:postId" element={<BlogPostPage />} />
        <Route path="/me/blog/:postId/edit" element={<BlogEditPage />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/astro" element={<AstroMapPage />} />
        {/* 運勢試作（九星気学・四柱推命）。ナビ導線なしの評価用 */}
        <Route path="/unsei" element={<UnseiPage />} />
        <Route path="/credits" element={<CreditsPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/start" element={LOGIN_OPEN ? <LoginPage /> : <Navigate to="/" replace />} />
        <Route path="/login" element={LOGIN_OPEN ? <LoginPage /> : <Navigate to="/" replace />} />

        {/* admin（RequireAdmin 配下） */}
        <Route element={<RequireAdmin />}>
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/settings/style" element={<AdminStylePage />} />
          <Route path="/admin/nearby" element={<AdminNearbyPage />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/users/:uid" element={<AdminUserDetailPage />} />
          <Route path="/admin/feedback" element={<AdminFeedbackPage />} />
          <Route path="/admin/shrine-pin" element={<AdminShrinePinPage />} />
          <Route path="/admin/shrines" element={<AdminShrinesPage />} />
          <Route path="/admin/shrine-reports" element={<AdminShrineReportsPage />} />
          <Route path="/admin/chat-cost-sim" element={<AdminChatCostSimPage />} />
          <Route path="/admin/dev-cost-sim" element={<AdminDevCostSimPage />} />
          <Route path="/admin/deities" element={<AdminDeitiesPage />} />
          <Route path="/admin/miles" element={<AdminMilePage />} />
          <Route path="/admin/exchange-items" element={<AdminExchangeItemsPage />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* フルスクリーン（AppShell外） */}
      {/* v1の「自分の神社」= /keidai（境内）。island-field は引退（コードは残置、/me/shrine・/guide-field 同様） */}
      <Route
        path="/keidai"
        element={
          <Suspense fallback={<DarkFallback />}>
            <KeidaiFieldPage />
          </Suspense>
        }
      />
      {/* 土地の頁（試作）: いまの町字 → 江戸の村の記録。ナビ導線なしの評価用 */}
      <Route
        path="/tochi"
        element={
          <Suspense fallback={<DarkFallback />}>
            <TochiPage />
          </Suspense>
        }
      />
    </Routes>
  );
}
